import type { TwoGisNativeSelectionPayloadStable } from '@/features/outlet/model/two-gis-native-payload';

import { useMobileVersion } from '@/shared/lib/use-mobile-version';

const messageHandler = 'nativeApp';
const eventHandler = 'nativeHandler';

export enum LISTENER_ACTION_NATIVE {
  ALLOW_PUSH_NOTIFICATIONS = 'allowPushNotifications',
  REFRESH_DATA = 'refreshData',
  CHANGE_ORG_REFERENCE_ID = 'changeOrgReferenceId',
  NAVIGATE_BACK = 'navigateBack',
  BOTTOM_SHEET_ACTION = 'bottomSheetAction',
  SET_2GIS_SELECTION = 'set2gisSelection',
  CHANGE_WEB_LANGUAGE = 'changeWebLanguage',
  EMPLOYEE_SECURE_UPDATED = 'employeeSecureUpdated',
  HIDE_BALANCE = 'hideBalance',
}

export enum STATUS_BOTTOM_SHEET {
  ERROR,
  INFO, // без иконки
  WARNING,
  SUCCESS,
}

export enum NATIVE_TABS {
  MAIN,
  PAYMENTS, // без иконки
  OUTLET,
  PRODUCTS,
  SETTINGS,
}

export enum NATIVE_PAGES {
  CHANGE_PHONE = 'changePhone',
  CHANGE_PASSWORD = 'changePassword',
  CODE_WORD = 'codeWord',
}

type AppsFlyerAction =
  | 'ob_sale_point_tabbar_intent'
  | 'ob_sale_point_created'
  | 'ob_pos_intent_application'
  | 'ob_pos_application_sent'
  | 'ob_payda_intent_products'
  | 'ob_payda_intent_application'
  | 'ob_payda_application_sent'
  | 'ob_tez_intent_products';

declare global {
  interface Window {
    [key: string]: any;
  }
}

export function createNativeMessage(methodName: string) {
  return function <T extends unknown[]>(...args: T) {
    if (window[messageHandler]) window[messageHandler][methodName](...args);
    else if (window['webkit'] && window['webkit'].messageHandlers && window['webkit'].messageHandlers[messageHandler]) {
      window['webkit'].messageHandlers[messageHandler].postMessage({
        [methodName]: args[0] || '',
      });
    }
  };
}

export const nativeApp = {
  setOrgReferenceId(id: string | null) {
    return createNativeMessage('setOrgReferenceId')(id);
  },
  redirectHistoryPayment() {
    return createNativeMessage('redirectHistoryPayment')();
  },

  localStorageLoaded() {
    return createNativeMessage('localStorageLoaded')();
  },

  logout() {
    return createNativeMessage('logout')();
  },

  sessionExpired() {
    return createNativeMessage('sessionExpired')();
  },

  redirectToSign() {
    return createNativeMessage('redirectToSign')();
  },

  goToTab(tab: NATIVE_TABS) {
    return createNativeMessage('goToTab')(tab.toString());
  },

  goToPage(page: NATIVE_PAGES) {
    return createNativeMessage('goToPage')(page.toString());
  },

  downloadFile(base64content: string) {
    return createNativeMessage('downloadFile')(base64content);
  },

  downloadFileUrl(url: string) {
    return createNativeMessage('downloadFileUrl')(url);
  },

  downloadFileExcel(base64content: string) {
    return createNativeMessage('downloadFileExcel')(base64content);
  },

  openWhatsApp(number: string) {
    return createNativeMessage('openWhatsApp')(number.replace(/[+() \s]/g, ''));
  },

  openCall(number: string) {
    return createNativeMessage('openCall')(number);
  },

  openMail(mail: string) {
    return createNativeMessage('openMail')(mail);
  },

  refresh() {
    return createNativeMessage('refresh')();
  },

  switchPushNotifications(status: boolean) {
    return createNativeMessage('switchPushNotifications')(status);
  },

  backStep() {
    return createNativeMessage('backStep')();
  },

  sharedQrCode(base64: string) {
    return createNativeMessage('sharedQrCode')(base64);
  },

  navigate(url: string) {
    return createNativeMessage('navigate')(url);
  },

  swipeRefreshEnabled(value: boolean) {
    console.info('swipeRefreshEnabled', value);
    createNativeMessage('swipeRefreshEnabled')(value.toString());
  },

  showBottomSheet(payload: {
    subtitle: string;
    title: string;
    mainAction: string;
    labelMainAction: string;
    dismissAction: string;
    labelDismissAction: string;
    status: STATUS_BOTTOM_SHEET;
    persistent: boolean;
  }) {
    console.info('showBottomSheet');
    createNativeMessage('showBottomSheet')(JSON.stringify(payload));
  },

  loading(value: boolean) {
    return createNativeMessage('loading')(value.toString());
  },

  openInsideCamera() {
    return createNativeMessage('openInsideCamera')();
  },

  openOutsideCamera() {
    return createNativeMessage('openOutsideCamera')();
  },

  changeLanguage() {
    return createNativeMessage('changeLanguage')();
  },

  qrScannerScreen() {
    return createNativeMessage('qrScannerScreen')();
  },

  send2gisSelection(payload: TwoGisNativeSelectionPayloadStable) {
    return createNativeMessage('send2gisSelection')(JSON.stringify(payload));
  },

  updateEmployeeSecure() {
    return createNativeMessage('updateEmployeeSecure')();
  },

  updateSalePoint() {
    return createNativeMessage('updateSalePoint')();
  },

  updateCashier() {
    return createNativeMessage('updateCashier')();
  },

  uploadFile(type: string) {
    return createNativeMessage('uploadFile')(type);
  },

  signPayment(json: string) {
    console.log('jsAction', json);
    return createNativeMessage('signPayment')(json);
  },

  sendAppsFlyerEvent(action: AppsFlyerAction) {
    if (useMobileVersion(1122)) createNativeMessage('sendAppsFlyerEvent')(action);
  },
};

window.onload = function () {
  window[eventHandler] = {
    allowPushNotifications(status: boolean) {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.ALLOW_PUSH_NOTIFICATIONS, { detail: status }));
    },
    refreshData() {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.REFRESH_DATA));
    },
    changeOrgReferenceId(orgReferenceId: string) {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.CHANGE_ORG_REFERENCE_ID, { detail: orgReferenceId }));
    },
    changeWebLanguage(language: string) {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.CHANGE_WEB_LANGUAGE, { detail: language }));
    },
    navigateBack() {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.NAVIGATE_BACK));
    },
    bottomSheetAction(value: string) {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.BOTTOM_SHEET_ACTION, { detail: value }));
    },
    set2gisSelection(value: string) {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.SET_2GIS_SELECTION, { detail: value }));
    },
    setOutsidePhoto(base64: string) {
      window.dispatchEvent(new CustomEvent('setOutsidePhoto', { detail: base64 }));
    },
    setInsidePhoto(base64: string) {
      window.dispatchEvent(new CustomEvent('setInsidePhoto', { detail: base64 }));
    },
    employeeSecureUpdated(value: boolean) {
      window.dispatchEvent(new CustomEvent('employeeSecureUpdated', { detail: value }));
    },
    hideBalance(value: boolean) {
      window.dispatchEvent(new CustomEvent(LISTENER_ACTION_NATIVE.HIDE_BALANCE, { detail: value }));
    },
    setFile(base64: string) {
      window.dispatchEvent(new CustomEvent('setFile', { detail: base64 }));
    },
  };
};
